<script setup lang="ts">
    import { ref, computed } from 'vue'
    import { isNullOrWhitespace } from '@common/Common'

    import type { PanelistVoteReadModel } from '@Evaluation/Estimations/Domain/readmodels'

    // ---------------- props ----------------

    const props = defineProps<{
        AlternativeName: string
        Entries: PanelistVoteReadModel[]
        HasScale: boolean
        ScaleLevels: { Value: number }[]
    }>()

    // ---------------- local state ----------------

    const activeTab = ref<'votes' | 'summary' | 'notes'>('votes')

    // ---------------- derived ----------------

    const QuestionScale = computed<number[]>(() =>
        props.ScaleLevels.map(a => a.Value)
    )

    const VotesEntries = computed<PanelistVoteReadModel[]>(() =>
        props.Entries
    )

    const Comments = computed<PanelistVoteReadModel[]>(() =>
        VotesEntries.value.filter(a => !isNullOrWhitespace(a.Comment))
    )

    // ---------------- tab helpers ----------------

    const SummaryTabActive = computed(() => activeTab.value === 'summary')
    const VotesTabActive = computed(() => activeTab.value === 'votes')
    const CommentsTabActive = computed(() => activeTab.value === 'notes')

    function ShowSummaryTabCommand()
    {
        activeTab.value = 'summary'
    }

    function ShowVotesTabCommand()
    {
        activeTab.value = 'votes'
    }

    function ShowCommentsTabCommand()
    {
        activeTab.value = 'notes'
    }

    // ---------------- calculations ----------------

    const Average = computed<number | null>(() =>
    {
        const votes = VotesEntries.value.filter(a => a.Value != null)

        if (votes.length === 0)
            return null

        let sum = 0
        for (const v of votes)
            sum += v.Value!

        return sum / votes.length
    })

    const AverageCells = computed<TdViewModel[]>(() =>
    {
        const cells = QuestionScale.value.map(
            v => new TdViewModel(v, false, false)
        )

        if (Average.value != null)
        {
            const exact = QuestionScale.value.find(
                a => Math.round(100 * a) === Math.round(100 * Average.value!)
            )

            if (exact != null)
            {
                const cell = cells.find(c => c.Value === exact)!
                cell.Highlighted = true
            }
            else
            {
                const before = QuestionScale.value.filter(a => a < Average.value!).length

                const cell = cells[before - 1]
                cell.Highlighted = true
                cell.Value = Average.value!
                cell.Colspan = 2

                // Vue 3 migration rule: array.remove → splice
                cells.splice(before, 1)
            }
        }

        return cells
    })

    const HistogramValues = computed(() =>
    {
        const result: { Value: number; Count: number; PercentOfMax: number }[] = []

        const values = VotesEntries.value
            .filter(a => a.Value != null)
            .map(a => a.Value!)

        let maxCount = 0

        for (const value of QuestionScale.value)
        {
            const count = values.filter(v => v === value).length
            maxCount = Math.max(maxCount, count)
        }

        for (const value of QuestionScale.value)
        {
            const count = values.filter(v => v === value).length
            const percentOfMax = maxCount === 0 ? 0 : Math.round(count / maxCount * 100)

            result.push({
                Value: value,
                Count: count,
                PercentOfMax: percentOfMax
            })
        }

        return result
    })

    // ---------------- runtime VM ----------------

    class TdViewModel
    {
        Colspan: number
        Highlighted: boolean
        Value: number

        constructor(value: number, highlighted: boolean, colspan: boolean)
        {
            this.Colspan = colspan ? 2 : 1
            this.Highlighted = highlighted
            this.Value = value
        }
    }
</script>


<template>

    <div>
        <div v-if="HasScale" class="DirectRateVotesBrowserView">
            <ul class="nav nav-tabs">
                <li class="nav-item">
                    <a class="nav-link" @click="ShowVotesTabCommand" :class="{ 'active': VotesTabActive }">Votes</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ShowSummaryTabCommand" :class="{ 'active': SummaryTabActive }">Chart</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ShowCommentsTabCommand" :class="{ 'active': CommentsTabActive }">Comments</a>
                </li>
            </ul>

            <div class="tab-content">
                <div v-if="VotesTabActive" :class="{ 'active': VotesTabActive }" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <tbody>
                                <tr style="text-align: center;">
                                    <td style="text-align: left;">
                                        <strong>
                                            Average ({{Average != null ? Average.toFixedIfNeeded(2) : 'N/A'}})
                                        </strong>
                                    </td>
                                    <td v-for="c in AverageCells" :class="{ 'bg-color-gray': c.Highlighted, 'td-1': c.Colspan === 1, 'td-2': c.Colspan > 1 }" :colspan="c.Colspan">
                                        <span :class="{ 'fg-color-white': c.Highlighted }">{{c.Value.toFixedIfNeeded(2)}}</span>
                                    </td>
                                </tr>

                                <tr v-for="e in Entries" :key="e.ProjectUserId" style="text-align: center;">
                                    <td style="text-align: left;">
                                        {{e.ProjectUserName ?? '???'}}
                                    </td>
                                    <td v-for="lvl in QuestionScale" :class="{ 'bg-color-green': lvl === e.Value }">
                                        <span :class="{ 'fg-color-white': lvl === e.Value }">
                                            {{lvl}}
                                        </span>
                                    </td>
                                </tr>

                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="SummaryTabActive" :class="{ 'active': SummaryTabActive }" class="tab-pane pt-2">
                    <div class="table-responsive">
                        <table class="table">
                            <tbody>
                                <tr style="text-align: center;" class="bottom noborder">
                                    <td v-for="hv in HistogramValues" style="height: 400px; border: none; padding-top: 20px;">
                                        <div v-if="hv.Count">{{hv.Count}}</div>
                                        <div class="bg-color-green2" :style="{height: hv.PercentOfMax + '%'}"></div>
                                    </td>
                                </tr>
                                <tr style="text-align: center;">
                                    <td v-for="v in QuestionScale" style="width: 5%;">{{v.toFixedIfNeeded(2)}}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="CommentsTabActive" :class="{ 'active': CommentsTabActive }" class="tab-pane pt-2">
                    <blockquote v-for="c in Comments" class="blockquote">
                        <p class="mb-0">{{c.Comment}}</p>
                        <footer class="blockquote-footer">{{c.ProjectUserName ?? '???'}}</footer>
                    </blockquote>
                </div>
            </div>
        </div>

        <div v-if="!HasScale" class="DirectValueVotesBrowserView">
            <ul class="nav nav-tabs">
                <li class="nav-item">
                    <a class="nav-link" :class="{ 'active': VotesTabActive }" @click="ShowVotesTabCommand">Votes</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" :class="{ 'active': CommentsTabActive }" @click="ShowCommentsTabCommand">Comments</a>
                </li>
            </ul>
            <div class="tab-content">
                <div v-if="VotesTabActive" :class="{ 'active': VotesTabActive }" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <tbody>
                                <tr style="text-align: center;">
                                    <td style="text-align: left;">
                                        <strong>
                                            <span>Average</span>

                                        </strong>
                                    </td>
                                    <td>
                                        <span>{{Average != null ? Average.toFixedIfNeeded(2) : 'N/A'}}</span>
                                    </td>
                                </tr>

                                <tr v-for="e in VotesEntries" style="text-align: center;">
                                    <td style="text-align: left;">
                                        {{e.ProjectUserName ?? '???'}}
                                    </td>
                                    <td>
                                        {{e.Value}}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="CommentsTabActive" :class="{ 'active': CommentsTabActive }" class="tab-pane pt-2">
                    <blockquote class="blockquote" v-for="c in Comments">
                        <p class="mb-0">{{c.Comment}}</p>
                        <footer class="blockquote-footer">{{c.ProjectUserName ?? '???'}}</footer>
                    </blockquote>
                </div>
            </div>
        </div>
    </div>



</template>

<style scoped>
    .DirectRateVotesBrowserView tr.bottom td {
        vertical-align: bottom !important;
    }

    .DirectRateVotesBrowserView td.td-1 {
        width: 5%;
    }

    .DirectRateVotesBrowserView td.td-2 {
        width: 10%;
    }
</style>